/**
 * toolMeta — how a tool call is labelled in a transcript: its icon, its
 * short name and a one-line preview of its arguments.
 *
 * One place for it: the chat, a task's transcript and the landing page's
 * demo all render the same chip (ToolCallBubble), so a tool looks the same
 * everywhere it appears.
 *
 * The icon is the tool's own declaration (``Tool.icon`` on the backend: every
 * built-in sets one, MCP manifests set one per tool or server). A word
 * maps to a glyph here; the table is typed against the backend's list, so a
 * word added there without a glyph fails this build. Only a tool that
 * declares nothing (a LangChain bridge, the landing demo) is guessed from
 * its name, and the guess lands on the same words.
 */
import {
  Bell,
  Bot,
  Brain,
  Clock,
  Database,
  FileText,
  GitBranch,
  Globe,
  LayoutDashboard,
  ListChecks,
  Mic,
  Monitor,
  Plug,
  Rocket,
  ShieldCheck,
  Sparkles,
  SquareTerminal,
  Users,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import type { ToolIcon } from "@/lib/api-types";

const GLYPHS: Record<ToolIcon, LucideIcon> = {
  agent: Bot,
  code: SquareTerminal,
  data: Database,
  deploy: Rocket,
  device: Monitor,
  file: FileText,
  git: GitBranch,
  integration: Plug,
  memory: Brain,
  notify: Bell,
  pane: LayoutDashboard,
  people: Users,
  security: ShieldCheck,
  skill: Sparkles,
  task: ListChecks,
  time: Clock,
  voice: Mic,
  web: Globe,
};

// The fallback guess, on the tool's own name (``toolShortName``: without
// the ``core.`` / ``mcp.`` prefix, which matched "mcp" for every MCP tool).
// First match wins, so narrower words come before broader ones.
const GUESSES: ReadonlyArray<readonly [RegExp, ToolIcon]> = [
  [/memory|remember|recall/, "memory"],
  [/skill/, "skill"],
  [/pane/, "pane"],
  [/(^|[._])git(hub|lab)?([._]|$)/, "git"],
  [/sandbox|coder|code|exec|shell|terminal/, "code"],
  [/spawn|delegate|agent|researcher|concierge/, "agent"],
  [/task/, "task"],
  [/device|desktop/, "device"],
  [/search|web|http|fetch|browse|url/, "web"],
  [/artifact|file|storage|document|summar/, "file"],
  [/sql|postgres|database|query|table/, "data"],
  [/notify|mail|message|slack/, "notify"],
  [/integration|proxy|connector/, "integration"],
  [/time|clock|calendar|schedule/, "time"],
  [/publish|deploy|release/, "deploy"],
  [/guard|review|approv|policy/, "security"],
  [/user|team|directory|contact/, "people"],
];

function isToolIcon(word: string): word is ToolIcon {
  return Object.prototype.hasOwnProperty.call(GLYPHS, word);
}

/** The glyph for a call: what the tool declares, else a guess, else a wrench. */
export function toolIcon(name: string, declared?: string | null): LucideIcon {
  // A word this build does not know (a newer backend) falls through to the
  // guess rather than drawing nothing.
  if (declared && isToolIcon(declared)) return GLYPHS[declared];
  const short = toolShortName(name).toLowerCase();
  const guess = GUESSES.find(([re]) => re.test(short))?.[1];
  return guess ? GLYPHS[guess] : Wrench;
}

/** ``core.search_web`` → ``search_web``; ``mcp.github.create_issue`` →
 *  ``github.create_issue``. The full id stays in the chip's tooltip. */
export function toolShortName(name: string): string {
  return name.replace(/^(core|mcp)[._]/, "") || "tool";
}

function previewValue(value: unknown): string {
  if (typeof value === "string") return JSON.stringify(value.length > 40 ? `${value.slice(0, 40)}…` : value);
  if (Array.isArray(value)) return `[${value.length}]`;
  if (value !== null && typeof value === "object") return "{…}";
  return String(value);
}

/** ``query: "omni", limit: 5`` — the first arguments, cut at ``max``. */
export function argsPreview(args: Record<string, unknown>, max = 72): string {
  const text = Object.entries(args)
    .map(([k, v]) => `${k}: ${previewValue(v)}`)
    .join(", ");
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.max(0, Math.round(ms))}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

/** Milliseconds between two ISO timestamps, or undefined if either is missing. */
export function durationBetween(start: unknown, end: unknown): number | undefined {
  if (typeof start !== "string" || typeof end !== "string" || !start || !end) return undefined;
  const ms = Date.parse(end) - Date.parse(start);
  return Number.isFinite(ms) ? ms : undefined;
}
